import { Injectable } from '@angular/core';

export type NotificationType =
  | 'WatchMeActivate'
  | 'Panic'
  | 'CewArc'
  | 'CewArm'
  | 'CewTrigger'
  | 'CewSwitchEnergize'
  | 'CewTriggerEnergize'
  | 'CewRaised'
  | 'CewUnholstered'
  | 'CewWarning'
  | 'VehicleAcceleration'
  | 'AsuWeaponRack'
  | 'AsuWeaponRack1'
  | 'AsuWeaponRack2'
  | 'Holster'
  | 'Gunshot'
  | 'AsuLightbar'
  | 'AsuSiren'
  | 'VehicleVelocity'
  | 'AsuLightbarCode1'
  | 'AsuLightbarCode2'
  | 'AsuLightbarCode3'
  | 'AsuRearDoorDriver'
  | 'AsuRearDoorPassenger'
  | 'Baton'
  | 'PepperSpray'
  | 'Handcuff';

export interface NotificationIconConfig {
  type: 'font' | 'svg';
  value: string;
}

// New pipeline format mapping: assumes backend sends "com.axon.alert.signalactivation." + NotificationType.toLowerCase()
const NEW_PIPELINE_TYPE_MAP: Record<string, string> = {
  'com.axon.alert.signalactivation.watchmeactivate': 'WatchMeActivate',
  'com.axon.alert.signalactivation.panic': 'Panic',
  'com.axon.alert.signalactivation.cewarc': 'CewArc',
  'com.axon.alert.signalactivation.cewarm': 'CewArm',
  'com.axon.alert.signalactivation.cewtrigger': 'CewTrigger',
  'com.axon.alert.signalactivation.cewswitchenergize': 'CewSwitchEnergize',
  'com.axon.alert.signalactivation.cewtriggerenergize': 'CewTriggerEnergize',
  'com.axon.alert.signalactivation.cewraised': 'CewRaised',
  'com.axon.alert.signalactivation.cewunholstered': 'CewUnholstered',
  'com.axon.alert.signalactivation.cewwarning': 'CewWarning',
  'com.axon.alert.signalactivation.vehicleacceleration': 'VehicleAcceleration',
  'com.axon.alert.signalactivation.asuweaponrack': 'AsuWeaponRack',
  'com.axon.alert.signalactivation.asuweaponrack1': 'AsuWeaponRack1',
  'com.axon.alert.signalactivation.asuweaponrack2': 'AsuWeaponRack2',
  'com.axon.alert.signalactivation.holster': 'Holster',
  'com.axon.alert.signalactivation.gunshot': 'Gunshot',
  'com.axon.alert.signalactivation.asulightbar': 'AsuLightbar',
  'com.axon.alert.signalactivation.asusiren': 'AsuSiren',
  'com.axon.alert.signalactivation.vehiclevelocity': 'VehicleVelocity',
  'com.axon.alert.signalactivation.asulightbarcode1': 'AsuLightbarCode1',
  'com.axon.alert.signalactivation.asulightbarcode2': 'AsuLightbarCode2',
  'com.axon.alert.signalactivation.asulightbarcode3': 'AsuLightbarCode3',
  'com.axon.alert.signalactivation.asureardoordriver': 'AsuRearDoorDriver',
  'com.axon.alert.signalactivation.asureardoorpassenger': 'AsuRearDoorPassenger',
  'com.axon.alert.signalactivation.baton': 'Baton',
  'com.axon.alert.signalactivation.pepperspray': 'PepperSpray',
  'com.axon.alert.signalactivation.handcuff': 'Handcuff',
};

@Injectable({
  providedIn: 'root',
})
export class AxonNotificationIconService {
  private readonly iconMappings: Record<NotificationType, NotificationIconConfig> = {
    WatchMeActivate: { type: 'font', value: 'fa-hand' },
    Panic: { type: 'svg', value: 'panic' },
    CewArc: { type: 'font', value: 'fa-bolt' },
    CewArm: { type: 'font', value: 'fa-bolt' },
    CewTrigger: { type: 'font', value: 'fa-bolt' },
    CewSwitchEnergize: { type: 'font', value: 'fa-bolt' },
    CewTriggerEnergize: { type: 'font', value: 'fa-bolt' },
    CewRaised: { type: 'font', value: 'fa-bolt' },
    CewUnholstered: { type: 'font', value: 'fa-bolt' },
    CewWarning: { type: 'font', value: 'fa-bolt' },
    VehicleAcceleration: { type: 'font', value: 'fa-burst' },
    AsuWeaponRack: { type: 'font', value: 'fa-gun' },
    AsuWeaponRack1: { type: 'font', value: 'fa-gun' },
    AsuWeaponRack2: { type: 'font', value: 'fa-gun' },
    Holster: { type: 'font', value: 'fa-gun' },
    Gunshot: { type: 'font', value: 'fa-gun' },
    AsuLightbar: { type: 'font', value: 'fa-car-on' },
    AsuSiren: { type: 'font', value: 'fa-car-on' },
    VehicleVelocity: { type: 'font', value: 'fa-car-on' },
    AsuLightbarCode1: { type: 'font', value: 'fa-car-on' },
    AsuLightbarCode2: { type: 'font', value: 'fa-car-on' },
    AsuLightbarCode3: { type: 'font', value: 'fa-car-on' },
    AsuRearDoorDriver: { type: 'font', value: 'fa-car-on' },
    AsuRearDoorPassenger: { type: 'font', value: 'fa-car-on' },
    Baton: { type: 'font', value: 'fa-wand' },
    PepperSpray: { type: 'font', value: 'fa-spray-can-sparkles' },
    Handcuff: { type: 'font', value: 'fa-handcuffs' },
  };

  getIconConfig(notificationType: string): NotificationIconConfig {
    return this.iconMappings[notificationType as NotificationType] 
      ?? this.iconMappings[NEW_PIPELINE_TYPE_MAP[notificationType] as NotificationType]
      ?? { type: 'font', value: 'fa-bell' };
  }

  getIconClass(notificationType: string): string {
    const config = this.getIconConfig(notificationType);
    return config.type === 'font' ? config.value : '';
  }

  generateIconHTML(notificationType: string): string {
    const config = this.getIconConfig(notificationType);

    if (config.type === 'svg') {
      // For SVG icons, use img tag with asset URL (safer than inline SVG)
      return this.generateSvgIconHTML(config.value);
    } else {
      // Generate font icon HTML for font icons
      return `<i class="fa-solid ${config.value} fs-notification-banner-icon"></i>`;
    }
  }

  private generateSvgIconHTML(iconName: string): string {
    // Sanitize iconName to prevent path traversal attacks
    const sanitizedIconName = iconName.replace(/[^a-zA-Z0-9-]/g, '');
    const assetPath = `assets/fusus-icons/SVG/${sanitizedIconName}-alert.svg`;

    return `<img src="${assetPath}" class="fs-notification-banner-icon" style="vertical-align: middle; display: inline-block;" alt="${sanitizedIconName} alert" loading="lazy">`;
  }
}
